import React, { useState, useEffect, useCallback } from 'react';
import { reportsAPI, departmentsAPI } from '../../../api/client';
import { useAuth } from '../../../context/AuthContext';
import {
  EnvelopeIcon, ClockIcon, CalendarDaysIcon, PaperAirplaneIcon,
  CheckCircleIcon, ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// ── Schedule constants ────────────────────────────────────────────────────────

const FREQUENCY_OPTIONS = [
  { value: 'daily',   label: 'Daily' },
  { value: 'weekly',  label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => {
  const n = i + 1;
  const suffix = n === 1 || n === 21 || n === 31 ? 'st'
    : n === 2 || n === 22 ? 'nd'
    : n === 3 || n === 23 ? 'rd' : 'th';
  return { value: n, label: `${n}${suffix}` };
});

const HOURS_12 = Array.from({ length: 12 }, (_, i) => {
  const h = i === 0 ? 12 : i;
  return { value: h, label: String(h) };
});

const MINUTES = [
  { value: 0,  label: '00' },
  { value: 15, label: '15' },
  { value: 30, label: '30' },
  { value: 45, label: '45' },
];

/** "HH:MM" (24h) → { hour12, minute, ampm } */
function parse24(t) {
  const [hStr, mStr] = (t || '08:00').split(':');
  const h24 = parseInt(hStr, 10) || 0;
  const minute = parseInt(mStr, 10) || 0;
  const ampm = h24 < 12 ? 'AM' : 'PM';
  const hour12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return { hour12, minute, ampm };
}

/** { hour12, minute, ampm } → "HH:MM" (24h) */
function to24({ hour12, minute, ampm }) {
  let h = hour12 % 12;
  if (ampm === 'PM') h += 12;
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

// ── Small UI primitives ───────────────────────────────────────────────────────

function Toggle({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer select-none">
      <div
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-5 rounded-full transition-colors ${checked ? 'bg-primary-600' : 'bg-gray-300'}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
      </div>
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}

function Checkbox({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer select-none">
      <div
        onClick={() => onChange(!checked)}
        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${checked ? 'bg-primary-600 border-primary-600' : 'border-gray-300'}`}
      >
        {checked && (
          <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none">
            <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}

function SelBox({ value, onChange, options, className = '' }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300 ${className}`}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-5">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-4">
        <span className="text-primary-600">{icon}</span>
        {title}
      </h3>
      {children}
    </div>
  );
}

// ── Default form state ────────────────────────────────────────────────────────

const DEFAULT = {
  auto_email_enabled: false,
  recipient_emails: '',
  schedule_frequency: 'weekly',
  schedule_day: 0,
  schedule_day_of_month: 1,
  schedule_time: '08:00',
  rolling_window_days: 7,
  report_subject: '',
  include_candidate_details: true,
  include_interview_time: true,
  include_interviewer: true,
  include_status: true,
};

// ── Main component ────────────────────────────────────────────────────────────

export default function HRReportsSection() {
  const { canModule } = useAuth();
  const canEdit = canModule('hr_reports', 'edit');
  const canSend = canModule('hr_reports', 'add');
  const [departments, setDepartments]   = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [settings, setSettings]         = useState(DEFAULT);
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [sending, setSending]           = useState(false);
  const [dirty, setDirty]               = useState(false);

  // Decomposed time state (kept in sync with settings.schedule_time)
  const [timeParts, setTimeParts] = useState(parse24(DEFAULT.schedule_time));

  const loadDepts = useCallback(async () => {
    try {
      const res = await departmentsAPI.list();
      setDepartments(res.data);
      if (res.data.length > 0) setSelectedDept((prev) => prev ?? res.data[0].id);
    } catch {
      toast.error('Failed to load departments');
    }
  }, []);

  useEffect(() => { loadDepts(); }, []);

  const loadSettings = useCallback(async (deptId) => {
    if (!deptId) return;
    setLoading(true);
    try {
      const res = await reportsAPI.getSettings(deptId);
      const s = { ...DEFAULT, ...res.data };
      setSettings(s);
      setTimeParts(parse24(s.schedule_time));
      setDirty(false);
    } catch {
      toast.error('Failed to load report settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedDept) loadSettings(selectedDept); }, [selectedDept]);

  const patch = (field, value) => {
    setSettings((s) => ({ ...s, [field]: value }));
    setDirty(true);
  };

  const patchTime = (part, raw) => {
    const val = part === 'minute' ? parseInt(raw, 10) : part === 'hour12' ? parseInt(raw, 10) : raw;
    const next = { ...timeParts, [part]: val };
    setTimeParts(next);
    setSettings((s) => ({ ...s, schedule_time: to24(next) }));
    setDirty(true);
  };

  const save = async () => {
    if (!selectedDept) return;
    setSaving(true);
    try {
      const res = await reportsAPI.updateSettings(selectedDept, {
        auto_email_enabled:       settings.auto_email_enabled,
        recipient_emails:         settings.recipient_emails,
        schedule_frequency:       settings.schedule_frequency,
        schedule_day:             settings.schedule_day,
        schedule_day_of_month:    settings.schedule_day_of_month,
        schedule_time:            settings.schedule_time,
        rolling_window_days:      settings.rolling_window_days,
        report_subject:           settings.report_subject,
        include_candidate_details: settings.include_candidate_details,
        include_interview_time:   settings.include_interview_time,
        include_interviewer:      settings.include_interviewer,
        include_status:           settings.include_status,
      });
      const s = { ...DEFAULT, ...res.data };
      setSettings(s);
      setTimeParts(parse24(s.schedule_time));
      setDirty(false);
      toast.success('Settings saved — schedule updated');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    if (!selectedDept) return;
    if (!settings.recipient_emails.trim()) {
      toast.error('Add at least one recipient email before sending');
      return;
    }
    setSending(true);
    try {
      await reportsAPI.sendNow(selectedDept);
      toast.success('Report is being generated and emailed — check your inbox shortly');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const deptName = departments.find((d) => d.id === selectedDept)?.name || '';

  // ── Schedule summary label ────────────────────────────────────────────────
  const schedLabel = (() => {
    const { hour12, minute, ampm } = timeParts;
    const tStr = `${hour12}:${String(minute).padStart(2, '0')} ${ampm}`;
    const freq = settings.schedule_frequency;
    if (freq === 'daily') return `every day at ${tStr}`;
    if (freq === 'monthly') {
      const dom = DAYS_OF_MONTH.find((d) => d.value === settings.schedule_day_of_month)?.label || '1st';
      return `on the ${dom} of each month at ${tStr}`;
    }
    const dow = DAYS_OF_WEEK.find((d) => d.value === settings.schedule_day)?.label || 'Monday';
    return `every ${dow} at ${tStr}`;
  })();

  // ── Next-period preview ───────────────────────────────────────────────────
  // Mirrors the backend _rolling_range(): last N complete days ending yesterday.
  const nextPeriodLabel = (() => {
    if (settings.schedule_frequency === 'monthly') {
      return 'the previous calendar month';
    }
    const days = Math.max(1, parseInt(settings.rolling_window_days, 10) || 7);
    const fmt = (d) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    const to = new Date();
    to.setDate(to.getDate() - 1);               // yesterday
    const from = new Date(to);
    from.setDate(from.getDate() - (days - 1));   // N complete days
    return `${fmt(from)} – ${fmt(to)}`;
  })();

  return (
    <div className="space-y-6">
      {/* Department selector */}
      {departments.length > 1 && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Department</label>
          <select
            value={selectedDept || ''}
            onChange={(e) => setSelectedDept(Number(e.target.value))}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
          >
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary-600" />
        </div>
      ) : (
        <>
          {/* ── Recipients ───────────────────────────────────────────── */}
          <Section icon={<EnvelopeIcon className="w-4 h-4" />} title="Recipients">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Recipient email addresses <span className="text-gray-400">(comma or newline separated)</span>
            </label>
            <textarea
              value={settings.recipient_emails}
              onChange={(e) => patch('recipient_emails', e.target.value)}
              placeholder="hr@example.com, manager@example.com"
              rows={3}
              className="w-full resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
            />
          </Section>

          {/* ── Schedule ─────────────────────────────────────────────── */}
          <Section icon={<CalendarDaysIcon className="w-4 h-4" />} title="Auto-Send Schedule">
            <div className="space-y-5">
              <Toggle
                checked={settings.auto_email_enabled}
                onChange={(v) => patch('auto_email_enabled', v)}
                label="Enable automatic report emails"
              />

              {settings.auto_email_enabled && (
                <div className="space-y-4 pl-1">
                  {/* Frequency */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">Frequency</label>
                    <div className="flex gap-2">
                      {FREQUENCY_OPTIONS.map((f) => (
                        <button
                          key={f.value}
                          onClick={() => patch('schedule_frequency', f.value)}
                          className={`px-4 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                            settings.schedule_frequency === f.value
                              ? 'bg-primary-600 text-white border-primary-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Conditional day selector */}
                  {settings.schedule_frequency === 'weekly' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Day of week</label>
                      <SelBox
                        value={settings.schedule_day}
                        onChange={(v) => patch('schedule_day', parseInt(v, 10))}
                        options={DAYS_OF_WEEK}
                      />
                    </div>
                  )}

                  {settings.schedule_frequency === 'monthly' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Day of month</label>
                      <SelBox
                        value={settings.schedule_day_of_month}
                        onChange={(v) => patch('schedule_day_of_month', parseInt(v, 10))}
                        options={DAYS_OF_MONTH}
                        className="w-28"
                      />
                      <p className="mt-1 text-xs text-gray-400">
                        Months with fewer days will send on the last available day.
                      </p>
                    </div>
                  )}

                  {/* Reporting window (daily/weekly only) */}
                  {settings.schedule_frequency !== 'monthly' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Reporting window <span className="text-gray-400">(days)</span>
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={90}
                        value={settings.rolling_window_days}
                        onChange={(e) => patch('rolling_window_days', Math.max(1, parseInt(e.target.value, 10) || 1))}
                        className="w-24 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
                      />
                      <p className="mt-1 text-xs text-gray-400">
                        Each report covers the last {settings.rolling_window_days} complete day
                        {settings.rolling_window_days === 1 ? '' : 's'} ending yesterday (rolling window).
                      </p>
                    </div>
                  )}

                  {/* Time picker */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Time</label>
                    <div className="flex items-center gap-1.5">
                      {/* Hour */}
                      <SelBox
                        value={timeParts.hour12}
                        onChange={(v) => patchTime('hour12', v)}
                        options={HOURS_12}
                        className="w-16"
                      />
                      <span className="text-gray-400 font-medium">:</span>
                      {/* Minute */}
                      <SelBox
                        value={timeParts.minute}
                        onChange={(v) => patchTime('minute', v)}
                        options={MINUTES}
                        className="w-16"
                      />
                      {/* AM / PM */}
                      <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                        {['AM', 'PM'].map((p) => (
                          <button
                            key={p}
                            onClick={() => patchTime('ampm', p)}
                            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                              timeParts.ampm === p
                                ? 'bg-primary-600 text-white'
                                : 'bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="space-y-1">
                    <p className="text-xs text-gray-400 flex items-center gap-1.5">
                      <ClockIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      Report will be sent <strong className="text-gray-600">{schedLabel}</strong>
                      {' '}(server time: Asia/Colombo)
                    </p>
                    <p className="text-xs text-gray-400 flex items-center gap-1.5">
                      <CalendarDaysIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      Next report will cover <strong className="text-gray-600">{nextPeriodLabel}</strong>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Section>

          {/* ── Subject ──────────────────────────────────────────────── */}
          <Section icon={<EnvelopeIcon className="w-4 h-4" />} title="Email Subject">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Subject line <span className="text-gray-400">(leave blank for default)</span>
            </label>
            <input
              type="text"
              value={settings.report_subject}
              onChange={(e) => patch('report_subject', e.target.value)}
              placeholder="HR Weekly Report — {date range}"
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
            />
          </Section>

          {/* ── Include / Exclude ─────────────────────────────────────── */}
          <Section icon={<CheckCircleIcon className="w-4 h-4" />} title="Include in Report Email">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Checkbox checked={settings.include_status}            onChange={(v) => patch('include_status', v)}            label="Screening status" />
              <Checkbox checked={settings.include_candidate_details} onChange={(v) => patch('include_candidate_details', v)} label="Candidate details" />
              <Checkbox checked={settings.include_interview_time}    onChange={(v) => patch('include_interview_time', v)}    label="Interview times" />
              <Checkbox checked={settings.include_interviewer}       onChange={(v) => patch('include_interviewer', v)}       label="Interviewer info" />
            </div>
          </Section>

          {/* ── Actions ──────────────────────────────────────────────── */}
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={save}
              disabled={saving || !dirty || !canEdit}
              title={canEdit ? '' : "🔒 You don't have permission"}
              className="flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving
                ? <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                : <CheckCircleIcon className="w-4 h-4" />}
              {saving ? 'Saving…' : 'Save Settings'}
            </button>

            <button
              onClick={sendNow}
              disabled={sending || !canSend}
              title={canSend ? '' : "🔒 You don't have permission"}
              className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sending
                ? <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                : <PaperAirplaneIcon className="w-4 h-4" />}
              {sending ? 'Sending…' : 'Send Now'}
            </button>

            {dirty && (
              <span className="text-xs text-amber-600 flex items-center gap-1">
                <ExclamationCircleIcon className="w-3.5 h-3.5" /> Unsaved changes
              </span>
            )}
          </div>

          <p className="text-xs text-gray-400 border-t border-gray-100 pt-4">
            "Send Now" generates a report covering the previous period and emails it immediately.
            An email account must be configured for{deptName ? ` ${deptName}` : ' this department'} in the Email section.
          </p>
        </>
      )}
    </div>
  );
}
